import React, { useState, useEffect, useRef } from 'react';
import { Volume2, VolumeX, Sparkles } from 'lucide-react';
import { isThemeSoundEnabled, setThemeSoundEnabled, playThemeSound } from '../../../utils/themeAudio';

const NEW_YEAR_CELEBRATIONS = [
    '🎆 HAPPY NEW YEAR! Level up your gaming legacy in 2026!',
    '🥂 POPPING THE CORK! Unstoppable victories await this year!',
    '⭐ 3... 2... 1... CELEBRATE! Happy New Year from PSH Store!',
    '🎊 NEW YEAR, NEW POWER! Claim your elite holiday perks!',
    '🍾 CHEERS TO 2026! May your gaming year be pure perfection!'
];

const ChampagneCelebration = ({ onTriggerConfettiStorm }) => {
    const [soundOn, setSoundOn] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [bannerMessage, setBannerMessage] = useState('');
    const [corkPopped, setCorkPopped] = useState(false);
    const bannerTimeoutRef = useRef(null);

    useEffect(() => {
        setSoundOn(isThemeSoundEnabled());
    }, []);

    const toggleSound = (e) => {
        e.stopPropagation();
        const next = !soundOn;
        setSoundOn(next);
        setThemeSoundEnabled(next);
        if (next) {
            playThemeSound('newyear', 'cheer');
        }
    };

    const handlePopCork = () => {
        setCorkPopped(true);
        setTimeout(() => setCorkPopped(false), 700);

        // Sound FX
        playThemeSound('newyear', 'corkPop');
        setTimeout(() => playThemeSound('newyear', 'cheer'), 200);

        // Trigger parent confetti storm
        if (onTriggerConfettiStorm) {
            onTriggerConfettiStorm();
        }

        // Random Celebration Message
        const msg = NEW_YEAR_CELEBRATIONS[Math.floor(Math.random() * NEW_YEAR_CELEBRATIONS.length)];
        setBannerMessage(msg);
        if (bannerTimeoutRef.current) clearTimeout(bannerTimeoutRef.current);
        bannerTimeoutRef.current = setTimeout(() => setBannerMessage(''), 3500);
    };

    return (
        <>
            {/* Global New Year Banner Popup */}
            {bannerMessage && (
                <div className="fixed top-16 md:top-20 inset-x-0 flex justify-center z-[75] pointer-events-none px-4">
                    <div className="w-full max-w-md animate-bounce">
                    <div className="bg-gradient-to-r from-amber-600 via-indigo-600 to-amber-600 text-white font-bold text-center px-5 py-3 rounded-2xl shadow-2xl shadow-indigo-950/80 border border-yellow-300/60 backdrop-blur-md flex items-center justify-center gap-2">
                        <Sparkles className="w-5 h-5 text-yellow-300 animate-spin" />
                        <span className="text-sm tracking-wide text-shadow">{bannerMessage}</span>
                    </div>
                </div>
                </div>
            )}

            {/* Bottom-left Champagne Mascot */}
            <div className="fixed bottom-5 left-4 md:left-8 z-[68] select-none flex flex-col items-start gap-2">
                {/* Speech Tooltip */}
                {hovered && !bannerMessage && (
                    <div className="bg-gray-950/95 text-yellow-400 text-xs font-semibold px-3 py-1.5 rounded-xl border border-yellow-500/50 shadow-lg shadow-black/60 animate-bounce whitespace-nowrap mb-1">
                        Pop the New Year Cork! 🍾
                        <div className="absolute -bottom-1 left-4 w-2 h-2 bg-gray-950 border-b border-r border-yellow-500/50 rotate-45" />
                    </div>
                )}

                <div className="flex items-center gap-2">
                    {/* The Interactive Champagne Bottle */}
                    <button
                        type="button"
                        onClick={handlePopCork}
                        onMouseEnter={() => setHovered(true)}
                        onMouseLeave={() => setHovered(false)}
                        className={`group relative outline-none focus:outline-none transition-all duration-300 ${
                            corkPopped ? 'animate-spooky-shake scale-125' : 'animate-float hover:scale-110'
                        }`}
                        title="Click to pop the celebration cork!"
                    >
                        {/* Golden Radiance Aura */}
                        <div className="absolute inset-0 rounded-full bg-yellow-500/30 blur-xl group-hover:bg-yellow-400/50 transition-all duration-500 pointer-events-none" />

                        {/* Champagne SVG */}
                        <svg width="54" height="54" viewBox="0 0 100 100" className="relative drop-shadow-[0_4px_16px_rgba(245,158,11,0.7)]">
                            {/* Bottle Body */}
                            <path d="M42 38 L42 22 L40 18 L60 18 L58 22 L58 38 C 68 45 74 60 74 76 C 74 88 64 94 50 94 C 36 94 26 88 26 76 C 26 60 32 45 42 38 Z" fill="#14532d" stroke="#166534" strokeWidth="2" />

                            {/* Gold Neck Foil */}
                            <path d="M41 34 L59 34 L59 20 L41 20 Z" fill="#fbbf24" stroke="#d97706" strokeWidth="1" />

                            {/* Golden Label on Body */}
                            <ellipse cx="50" cy="65" rx="16" ry="18" fill="#fef08a" stroke="#ca8a04" strokeWidth="1.5" />
                            <text x="50" y="66" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#78350f">2026</text>
                            <text x="50" y="76" textAnchor="middle" fontSize="6" fontWeight="bold" fill="#b45309">CHEERS</text>

                            {/* Flying Cork when popped */}
                            {corkPopped && (
                                <g className="animate-bounce">
                                    <ellipse cx="50" cy="6" rx="6" ry="4" fill="#d97706" />
                                    <circle cx="45" cy="12" r="2" fill="#ffffff" />
                                    <circle cx="55" cy="10" r="1.5" fill="#fef08a" />
                                </g>
                            )}

                            {/* Normal Cork */}
                            {!corkPopped && (
                                <rect x="44" y="11" width="12" height="7" rx="2" fill="#b45309" stroke="#78350f" strokeWidth="1" />
                            )}
                        </svg>
                    </button>

                    {/* Sound Toggle Pill */}
                    <button
                        type="button"
                        onClick={toggleSound}
                        className="px-2.5 py-1.5 rounded-full bg-gray-900/90 hover:bg-gray-800 text-yellow-400 border border-yellow-500/40 text-[11px] font-bold flex items-center gap-1.5 shadow-md shadow-black/40 transition-all hover:scale-105 active:scale-95"
                        title={soundOn ? 'Celebration Audio Enabled (Click to Mute)' : 'Enable New Year Audio'}
                    >
                        {soundOn ? (
                            <>
                                <Volume2 className="w-3.5 h-3.5 text-yellow-400 animate-pulse" />
                                <span className="hidden sm:inline">Audio ON</span>
                            </>
                        ) : (
                            <>
                                <VolumeX className="w-3.5 h-3.5 text-gray-400" />
                                <span className="hidden sm:inline text-gray-400">Audio OFF</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </>
    );
};

export default ChampagneCelebration;
